import React, { useState } from 'react';
import { View, Text, StyleSheet, Pressable, Platform } from 'react-native';
import WebScrollView from '../../components/WebScrollView';
import { Users } from '../../lib/icons';
import { Profile } from './types';
import { shadows } from '../../lib/styles';

interface PipelineViewProps {
  users: Profile[];
  onUserUpdate: (userId: string, newStatus: Profile['lifecycle_status']) => void;
  onUserClick?: (userId: string) => void;
}

const stages: Profile['lifecycle_status'][] = ['lead', 'walkthrough', 'first_visit', 'active', 'churned'];

const stageConfig = {
  lead: {
    title: 'Lead',
    backgroundColor: '#fef9c3',
    borderColor: '#fde047',
    textColor: '#a16207',
  },
  walkthrough: {
    title: 'Walkthrough',
    backgroundColor: '#dbeafe',
    borderColor: '#93c5fd',
    textColor: '#1e40af',
  },
  first_visit: {
    title: 'First Visit',
    backgroundColor: '#e0e7ff',
    borderColor: '#a5b4fc',
    textColor: '#3730a3',
  },
  active: {
    title: 'Active',
    backgroundColor: '#dcfce7',
    borderColor: '#86efac',
    textColor: '#166534',
  },
  churned: {
    title: 'Churned',
    backgroundColor: '#f9fafb',
    borderColor: '#e5e7eb',
    textColor: '#374151',
  },
};

const PipelineView: React.FC<PipelineViewProps> = ({ users, onUserUpdate, onUserClick }) => {
  const [draggedUserId, setDraggedUserId] = useState<string | null>(null);
  const [dragOverStage, setDragOverStage] = useState<Profile['lifecycle_status'] | null>(null);
  const [wasDragging, setWasDragging] = useState(false);

  const getUsersByStage = (stage: Profile['lifecycle_status']) => {
    return users.filter(user => user.lifecycle_status === stage);
  };

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, userId: string) => {
    if (Platform.OS === 'web') {
      setDraggedUserId(userId);
      setWasDragging(true);
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', userId);
      // Add a slight opacity to the dragged element
      if (e.currentTarget) {
        (e.currentTarget as HTMLElement).style.opacity = '0.5';
      }
    }
  };

  const handleDragEnd = (e: React.DragEvent<HTMLDivElement>) => {
    if (Platform.OS === 'web') {
      setDraggedUserId(null);
      setDragOverStage(null);
      // Restore opacity
      if (e.currentTarget) {
        (e.currentTarget as HTMLElement).style.opacity = '1';
      }
      // Reset wasDragging after a short delay to prevent click after drag
      setTimeout(() => setWasDragging(false), 100);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>, stage: Profile['lifecycle_status']) => {
    if (Platform.OS === 'web') {
      e.preventDefault();
      e.stopPropagation();
      if (draggedUserId) {
        e.dataTransfer.dropEffect = 'move';
        setDragOverStage(stage);
      }
    }
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    if (Platform.OS === 'web') {
      // Only clear if we're actually leaving the drop zone
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX;
      const y = e.clientY;
      
      if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
        setDragOverStage(null);
      }
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>, targetStage: Profile['lifecycle_status']) => {
    if (Platform.OS === 'web') {
      e.preventDefault();
      e.stopPropagation();
      const userId = e.dataTransfer.getData('text/plain') || draggedUserId;
      
      if (userId) {
        const user = users.find(u => u.id === userId);
        if (user && user.lifecycle_status !== targetStage) {
          onUserUpdate(userId, targetStage);
        }
      }
      
      setDraggedUserId(null);
      setDragOverStage(null);
    }
  };

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Users size={24} color="#9ca3af" />
        <Text style={styles.title}>Pipeline View</Text>
      </View>

      <WebScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.stagesContainer}>
        {stages.map(stage => {
          const config = stageConfig[stage];
          const stageUsers = getUsersByStage(stage);
          
          const isDragOver = dragOverStage === stage;
          
          const columnContent = (
            <>
              <View style={styles.stageHeader}>
                <Text style={[styles.stageTitle, { color: config.textColor }]}>
                  {config.title}
                </Text>
                <Text style={[styles.stageCount, { color: config.textColor }]}>
                  ({stageUsers.length})
                </Text>
              </View>

              <WebScrollView style={styles.usersList} showsVerticalScrollIndicator={false}>
                {stageUsers.map(user => {
                  const isDragging = draggedUserId === user.id;
                  const userCardContent = (
                    <>
                      <Text style={styles.userName}>{user.full_name || 'N/A'}</Text>
                      <Text style={styles.userPhone}>{user.phone_number || 'No phone'}</Text>
                      {(user.managed_home_count || 0) > 0 && (
                        <View style={styles.premiumBadge}>
                          <Text style={styles.premiumText}>
                            {(user.home_count || 0) > 1
                              ? `Premium ${user.managed_home_count}/${user.home_count}`
                              : 'Premium'}
                          </Text>
                        </View>
                      )}
                    </>
                  );

                  if (Platform.OS === 'web') {
                    return (
                      <div
                        key={user.id}
                        draggable={true}
                        onDragStart={(e: React.DragEvent<HTMLDivElement>) => handleDragStart(e, user.id)}
                        onDragEnd={(e: React.DragEvent<HTMLDivElement>) => handleDragEnd(e)}
                        onClick={(e: React.MouseEvent<HTMLDivElement>) => {
                          // Don't navigate if we just finished dragging
                          if (!wasDragging && onUserClick) {
                            onUserClick(user.id);
                          }
                        }}
                        style={{
                          ...StyleSheet.flatten(styles.userCard),
                          ...(isDragging ? StyleSheet.flatten(styles.userCardDragging) : {}),
                          cursor: 'pointer',
                          userSelect: 'none',
                          WebkitUserSelect: 'none',
                        } as any}
                      >
                        {userCardContent}
                      </div>
                    );
                  }

                  return (
                    <Pressable
                      key={user.id}
                      style={[
                        styles.userCard,
                        isDragging && styles.userCardDragging,
                      ]}
                      onPress={() => {
                        if (onUserClick) {
                          onUserClick(user.id);
                        }
                      }}
                    >
                      {userCardContent}
                    </Pressable>
                  );
                })}
              </WebScrollView>
            </>
          );

          if (Platform.OS === 'web') {
            const flatColumnStyle = StyleSheet.flatten([
              styles.stageColumn,
              {
                backgroundColor: isDragOver ? 'rgba(37, 99, 235, 0.1)' : config.backgroundColor,
                borderColor: config.borderColor,
              },
              isDragOver && styles.stageColumnDragOver,
            ]);
            
            return (
              <div
                key={stage}
                onDragOver={(e: React.DragEvent<HTMLDivElement>) => handleDragOver(e, stage)}
                onDragLeave={(e: React.DragEvent<HTMLDivElement>) => handleDragLeave(e)}
                onDrop={(e: React.DragEvent<HTMLDivElement>) => handleDrop(e, stage)}
                style={flatColumnStyle as any}
              >
                {columnContent}
              </div>
            );
          }

          return (
            <View
              key={stage}
              style={[
                styles.stageColumn,
                {
                  backgroundColor: isDragOver ? 'rgba(37, 99, 235, 0.1)' : config.backgroundColor,
                  borderColor: config.borderColor,
                },
                isDragOver && styles.stageColumnDragOver,
              ]}
            >
              {columnContent}
            </View>
          );
        })}
      </WebScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#ffffff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#f3f4f6',
    padding: 24,
    ...shadows.sm,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 24,
    gap: 12,
  },
  title: {
    fontSize: 20,
    fontWeight: '600',
    color: '#000000',
  },
  stagesContainer: {
    flexDirection: 'row',
  },
  stageColumn: {
    width: 200,
    minWidth: 200,
    flexShrink: 0,
    borderRadius: 8,
    borderWidth: 1,
    padding: 16,
    marginRight: 16,
    minHeight: 400,
    maxHeight: 600,
    ...(Platform.OS === 'web' && {
      transition: 'border-color 0.2s ease',
    }),
  },
  stageColumnDragOver: {
    borderWidth: 2,
    borderColor: '#2563eb',
  },
  stageHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
    gap: 8,
  },
  stageTitle: {
    fontSize: 16,
    fontWeight: '600',
  },
  stageCount: {
    fontSize: 14,
  },
  usersList: {
    flex: 1,
  },
  userCard: {
    backgroundColor: '#ffffff',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    padding: 12,
    marginBottom: 8,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
      userSelect: 'none',
      transition: 'opacity 0.2s ease, transform 0.2s ease',
    }),
  },
  userCardDragging: {
    opacity: 0.5,
    ...(Platform.OS === 'web' && {
      transform: 'scale(0.95)',
    }),
  },
  userName: {
    fontSize: 14,
    fontWeight: '600',
    color: '#000000',
    marginBottom: 4,
    ...(Platform.OS === 'web' && {
      display: 'block',
    }),
  },
  userPhone: {
    fontSize: 12,
    color: '#6b7280',
    marginBottom: 8,
  },
  premiumBadge: {
    alignSelf: 'flex-start',
    backgroundColor: '#000000',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
  },
  premiumText: {
    color: '#ffffff',
    fontSize: 12,
    fontWeight: '500',
  },
});

export default PipelineView;
